// Copyright 2018-2019 Unryo. All Rights Reserved.
import { click } from 'ol/events/condition'
import { ZoomToExtent } from 'ol/control'
import { Extent } from 'ol/extent'
import Feature, { FeatureLike } from 'ol/Feature'
import Point from 'ol/geom/Point'
import Select from 'ol/interaction/Select'
import TileLayer from 'ol/layer/Tile'
import VectorLayer from 'ol/layer/Vector'
import Map from 'ol/Map'
import Overlay from 'ol/Overlay'
import { fromLonLat } from 'ol/proj'
import Cluster from 'ol/source/Cluster'
import OSM from 'ol/source/OSM'
import VectorSource from 'ol/source/Vector'
import Icon from 'ol/style/Icon'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import Style, { StyleFunction } from 'ol/style/Style'
import Text from 'ol/style/Text'
import { NodeStatus, Severity, UnryoIssue } from '@/model/node'
import { findMostImportantIssue } from './issue'
import { MarkerColors } from '@/plugins/colors'

/**
 * A marker needs to contain its location and health status.
 *
 * It can also contain a bunch of other properties...
 */
interface Marker {
  lat: number
  lon: number
  health: {
    status: NodeStatus
    availabilityIssue: UnryoIssue
    performanceIssue: UnryoIssue
  }
  [s: string]: any
}

/**
 * Events callbacks for actions on the map.
 */
interface MapEvents {
  /**
   * A popup has been opened for one or more markers.
   *
   * @param items The markers for which the popup has been opened.
   */
  onPopupOpened: (items: Marker[]) => void
}

/**
 * Map actions that can be triggered externally.
 */
interface MapActions {
  /**
   * Issues a close request on the currently opened popup.
   */
  closePopup?: () => void
  /**
   * Resets markers with provided ones.
   *
   * @param markers New markers to display on the map.
   */
  reset: (markers: Marker[]) => void
}

/**
 * Initializes the map, adding the provided markers on it.
 *
 * @param target The container element for the map.
 * @param markers Markers that will appear on the map.
 * @param popup Popup container element.
 * @param events Listeners for specific map events like the popup being opened.
 * @returns Actions that can be triggered externally, like closing an opened popup.
 */
export function initMap (target: HTMLElement, markers: Marker[], popup: HTMLElement, events: MapEvents): MapActions {
  const map = new Map({
    target,
    layers: [
      new TileLayer({
        source: new OSM()
      })
    ]
  })

  // custom styling for markers
  const style = styleFunction()

  // once markers have been added, we can use their extent to properly zoom on the map
  const extent = addMarkers(map, markers, style)
  map.getView().fit(extent, { maxZoom: 10, padding: [ 256, 128, 32, 128 ] })
  const zoom = new ZoomToExtent({ extent })
  map.addControl(zoom)

  // actions that can be triggered externally
  const actions: MapActions = {
    reset (newMarkers: Marker[]) {
      actions.closePopup && actions.closePopup()
      map.getLayers().forEach((layer) => {
        if (layer instanceof VectorLayer) {
          map.removeLayer(layer)
        }
      })
      const newExtent = addMarkers(map, newMarkers, style);
      // overwrite protected field without redefinition
      (zoom as any).extent = newExtent
    }
  }

  addMarkersPopup(map, popup, style, events, actions)

  return actions
}

/**
 * Computes a cluster color based on its markers status.
 *
 * @param markers The cluster markers.
 * @returns The cluster color.
 */
function getClusterColor (markers: Marker[]): string {
  let color = 'FFC107'

  const status = new Set(markers.map((marker) => marker.health && marker.health.status))
  if (status.size === 1) {
    switch (status.keys().next().value) {
      case NodeStatus.ONLINE:
        color = MarkerColors.SUCCESS
        const severity = findMostImportantSeverity(markers)
        color = findMarkerColor(severity)
        break
      case NodeStatus.OFFLINE:
        color = 'FF5252'
        break
    }
  }

  return color
}

/**
 * Finds the most important severity issue of a cluster.
 *
 * @param markers The cluster markers.
 * @returns the most important severity.
 */
function findMostImportantSeverity (markers: Marker[]): Severity {
  const issues: UnryoIssue[] = []
  markers.forEach((marker) => {
    if (marker.health) {
      if (marker.health.availabilityIssue) {
        issues.push(marker.health.availabilityIssue)
      }
      if (marker.health.performanceIssue) {
        issues.push(marker.health.availabilityIssue)
      }
    }
  })
  const issue = findMostImportantIssue(...issues)
  if (issue) {
    return issue.severity
  }
  return Severity.SUCCESS
}


/**
 * Finds the marker color according to the most important issue severity.
 *
 * @param severity of the most important issue.
 * @returns the marker color.
 */
function findMarkerColor (severity: Severity): string {
  switch (severity) {
    case Severity.SUCCESS:
      return MarkerColors.SUCCESS
    case Severity.UNKNOWN:
      return MarkerColors.UNKNOWN
    case Severity.MINOR:
      return MarkerColors.MINOR
    case Severity.MAJOR:
      return MarkerColors.MAJOR
    case Severity.CRITICAL:
      return MarkerColors.CRITICAL
    default:
      return MarkerColors.UNKNOWN
  }
}

/**
 * Creates a cluster image based on its color.
 *
 * @param color The cluster color.
 * @returns The cluster image.
 */
function createClusterImage (color: string): Icon {
  return new Icon({
    anchor: [ .5, 1 ],
    scale: 2,
    // tslint:disable-next-line
    src: `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="%23${color}"/><circle cx="12" cy="9" r="4" fill="%23fff"/></svg>`
  })
}

/**
 * Computes the label scale based on its length so that is fits in the marker.
 *
 * @param label The label.
 * @returns The scale to apply.
 */
function computeLabelScale (label: string): number {
  switch (label.length) {
    case 1:
      return 1.5
    case 2:
      return 1.2
    case 3:
      return .8
    default:
      return .7
  }
}

/**
 * Creates a cluster style based on its image and marker count.
 *
 * @param image The cluster image.
 * @param size Number of markers underneath.
 * @returns A feature style.
 */
function createClusterStyle (image: Icon, size: number): Style {
  const text = size > 1000 ? Math.round(size / 1000) + ' K' : String(size)
  const scale = computeLabelScale(text)

  return new Style({
    image,
    text: new Text({
      fill: new Fill({
        color: '#000'
      }),
      offsetY: -29,
      scale,
      stroke: new Stroke({
        color: '#000',
        width: .5
      }),
      text
    })
  })
}

/**
 * Creates a dedicated style function for markers.
 *
 * @returns The function used to style markers.
 */
function styleFunction (): StyleFunction {
  // keep generated styles in cache for reuse purposes
  const styles: {[s: string]: { [s: string]: Style }} = {}

  // styles are based on amount of clustered markers and health status
  return (cluster: FeatureLike) => {
    const features = cluster.get('features')
    const markers: Marker[] = []
    features.forEach((marker: Feature) => {
      markers.push(marker.getProperties() as Marker)
    })
    const color = getClusterColor(markers)

    let style = styles[color] && styles[color][String(features.length)]
    if (!style) {
      const image = createClusterImage(color)
      style = createClusterStyle(image, features.length)
      if (!styles[color]) { styles[color] = {} }
      styles[color][String(features.length)] = style
    }
    return style
  }
}

/**
 * Adds markers to the map, in clusters.
 *
 * @param map Map instance to which markers will be added.
 * @param markers Marker metadata, including location.
 * @param style Style to be set for markers.
 * @return Marker view extent for auto-viewport purposes.
 */
function addMarkers (map: Map, markers: Marker[], style: StyleFunction): Extent {
  // features contain location and properties
  const features = markers.map((marker) => {
    const feature = new Feature(new Point(fromLonLat([marker.lon, marker.lat])))
    feature.setProperties(marker, true)
    return feature
  })

  // features are provided by sources, organized in clusters
  // the source is used to provide the default viewport based on markers location
  const source = new VectorSource({
    features
  })
  map.addLayer(new VectorLayer({
    source: new Cluster({
      distance: 42,
      source
    }),
    style
  }))
  return source.getExtent()
}

/**
 * Adds a popup overlay that displays markers properties.
 *
 * @param map Map instance on which the popup has to be added.
 * @param element Popup container element.
 * @param style Style to be set for selected markers.
 * @param events Listeners for specific map events like the popup being opened.
 * @param actions Actions that can be triggered externally, like closing an opened popup.
 */
function addMarkersPopup (
  map: Map,
  element: HTMLElement,
  style: StyleFunction,
  events: MapEvents,
  actions: MapActions
) {
  const overlay = new Overlay({
    element,
    autoPan: true,
    autoPanAnimation: {
      duration: 250
    }
  })
  map.addOverlay(overlay)

  const select = new Select({
    condition: click,
    style
  })
  map.addInteraction(select)

  // clicking on a feature should show a popup
  select.on('select', (e) => {
    // provide all items selected from all features under the current point
    const items: Marker[] = []
    select.getFeatures().forEach((cluster: Feature) => {
      cluster.get('features').forEach((marker: Feature) => {
        items.push(marker.getProperties() as Marker)
      })
    })
    if (items.length) {
      // align item on feature position, not mouse position
      const coordinate = (select.getFeatures().item(0).getGeometry() as any).getCoordinates()
      overlay.setPosition(coordinate)
      events.onPopupOpened(items)
    }
  })

  // update the cursor when on top of a feature that can be clicked on
  map.on('pointermove', (evt) => {
    map.getTargetElement().style.cursor = map.hasFeatureAtPixel(evt.pixel) ? 'pointer' : ''
  })

  actions.closePopup = () => {
    // by specification, overlays get closed through assigning undefined position
    (overlay as any).setPosition(undefined)
    // clear the selection so that the popup can be directly reopened
    select.getFeatures().clear()
  }
}
